import React, { memo } from 'react';
import { Badge, Form as BootstrapForm, InputGroup, FormControl } from 'react-bootstrap';
import { FaHashtag, FaFileInvoiceDollar, FaPlusSquare } from 'react-icons/fa';
import { BiPlus, BiChevronDown, BiChevronRight } from 'react-icons/bi';
import DynamicDatePicker from '../../core/shared/components/DynamicDatePicker';
import SupplierField from '../../core/shared/fields/SupplierField';
import CustomerSearchField from '../../users/CustomerSearchField';


// STABILIZED STYLES
const STYLES = {
    segment: {
        padding: '6px 10px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: '2px',
        flexShrink: 0,
        height: '100%',
        minWidth: 0,
        borderLeft: '1px solid rgba(255,255,255,0.15)'
    },
    label: {
        fontSize: '0.62rem',
        fontWeight: 900,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: '#ffffff',
        margin_bottom: '2px',
        opacity: 1
    },
    input: {
        height: '34px',
        background: 'rgba(255,255,255,0.08)',
        border: '1.5px solid rgba(255,255,255,0.25)',
        color: '#ffffff !important',
        fontWeight: 900,
        fontSize: '0.82rem !important',
        borderRadius: '6px !important'
    }
};

/* --- SHARED TOOLBAR CSS --- */
const ToolbarGlobalStyles = () => (
    <style>{`
        .sales-rep-select {
            background-color: rgba(255, 255, 255, 0.08) !important;
            border: 1.5px solid rgba(255, 255, 255, 0.25) !important;
            color: #ffffff !important;
            font-weight: 900 !important;
            font-size: 0.82rem !important;
            height: 34px !important;
            border-radius: 6px !important;
            cursor: pointer !important;
            padding-right: 30px !important;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='rgba(255,255,255,0.8)' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e") !important;
            background-repeat: no-repeat !important;
            background-position: right 0.75rem center !important;
            background-size: 16px 12px !important;
            -webkit-appearance: none !important;
            -moz-appearance: none !important;
            appearance: none !important;
        }
        .sales-rep-select:focus {
            background-color: rgba(255, 255, 255, 0.12) !important;
            border-color: rgba(255, 255, 255, 0.45) !important;
            box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.1) !important;
            outline: none !important;
        }
        .sales-rep-select option {
            background: #001c3a !important;
            color: white !important;
            font-weight: 600 !important;
        }

        /* ===== TOOLBAR DARK SEGMENT ===== */

.toolbar-dark-meta {
  background: #0f172a;
  border: 1px solid #1e293b;
  border-radius: 8px;
  padding: 12px 16px;
  position: relative;
  z-index: 3000;
}

.toolbar-label {
  display: block;
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #ffffff;
  margin-bottom: 8px;
}

/* Layout row */
.toolbar-row {
  display: flex;
  align-items: center;
  gap: 18px;
  width: 100%;
}

/* Field wrapper */
.toolbar-field {
  display: flex;
  flex-direction: column;
}

.fixed-110 {
  flex: 0 0 110px;
}

.flex-1 {
  flex: 1;
}

/* Field label */
.toolbar-field-label {
  font-size: 0.62rem;
  font-weight: 900;
  text-transform: uppercase;
  color: #ffffff;
  margin-bottom: 4px;
  letter-spacing: 0.4px;
}

.toolbar-field-label.subtle {
  color: #94a3b8;
}

/* ===== INPUT SYSTEM ===== */

.toolbar-input {
  height: 34px !important;
  background: #1e293b !important;
  border: 1px solid #334155 !important;
  color: #ffffff !important;
  font-size: 0.85rem !important;
  font-weight: 700 !important;
  border-radius: 6px !important;
  padding: 0 12px !important;
}

.toolbar-input::placeholder {
  color: rgba(255,255,255,0.35) !important;
  font-weight: 600;
}

.toolbar-input:focus {
  background: #1e293b !important;
  border-color: #64748b !important;
  box-shadow: none !important;
  color: #ffffff !important;
}

/* Prefix */
.toolbar-prefix {
  background: #1e293b !important;
  border: 1px solid #334155 !important;
  border-right: none !important;
  color: #ffffff !important;
  height: 34px !important;
  font-weight: 900 !important;
}

/* Remove Bootstrap override */
.toolbar-input-group .form-control {
  border-left: none !important;
}
    `}</style>
);

// --- SEGMENT 0: DYNAMIC HEADING ---
export const ToolbarHeading = memo(({ isEditMode, isDirect }) => (
    <div className="toolbar-segment" style={{ ...STYLES.segment, flex: '0 0 190px', borderLeft: 'none', borderRight: '1px solid rgba(255,255,255,0.15)' }}>
        <ToolbarGlobalStyles />
        <div className="d-flex align-items-center gap-2">
            <div style={{ background: '#0dcaf0', width: 4, height: 18, borderRadius: 2 }}></div>
            <h6 className="mb-0 fw-900 text-white text-uppercase" style={{ fontSize: '1.05rem', letterSpacing: '0.08em' }}>
                {isEditMode ? "Modifying" : "Registry"}
            </h6>
        </div>
        <div className="d-flex align-items-center gap-2 mt-1">
            <Badge bg="primary" style={{ fontSize: '0.65rem', fontWeight: 900, padding: '3px 8px' }} className="rounded-pill">
                {isDirect ? 'GRN' : 'PO'}
            </Badge>
            <span style={{ fontSize: '0.65rem', fontWeight: 900, opacity: 0.8, textTransform: 'uppercase', color: '#90caf9' }}>Purchase Order</span>
        </div>
    </div>
));

// --- SEGMENT A: MODE TOGGLE ---
export const ToolbarModeToggle = memo(({ purchaseType, onToggle }) => (
    <div className="toolbar-segment" style={{ ...STYLES.segment, minWidth: '120px', background: 'rgba(0,0,0,0.15)' }}>
        <span className="toolbar-label">Operational Mode</span>
        <div className="d-flex gap-1 p-1 bg-black bg-opacity-20 rounded-pill" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
            <button type="button" onClick={() => onToggle('Supplier')}
                style={{
                    background: purchaseType === 'Supplier' ? 'rgba(255,255,255,0.15)' : 'transparent',
                    border: purchaseType === 'Supplier' ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent',
                    boxShadow: purchaseType === 'Supplier' ? '0 0 10px rgba(255,255,255,0.1)' : 'none',
                    color: 'white', borderRadius: '15px', padding: '2px 10px', fontSize: '0.62rem', fontWeight: 900, cursor: 'pointer', flex: 1, transition: 'all 0.2s'
                }}>
                Supply
            </button>
            <button type="button" onClick={() => onToggle('Trade-In')}
                style={{
                    background: purchaseType === 'Trade-In' ? 'rgba(13,202,240,0.25)' : 'transparent',
                    border: purchaseType === 'Trade-In' ? '1px solid #0dcaf0' : '1px solid transparent',
                    boxShadow: purchaseType === 'Trade-In' ? '0 0 10px rgba(13,202,240,0.2)' : 'none',
                    color: purchaseType === 'Trade-In' ? '#0dcaf0' : 'rgba(255,255,255,0.4)', borderRadius: '15px', padding: '2px 10px', fontSize: '0.62rem', fontWeight: 900, cursor: 'pointer', flex: 1, transition: 'all 0.2s'
                }}>
                Trade
            </button>
        </div>
    </div>
));

// --- SEGMENT B: IDENTITY ---
export const ToolbarIdentity = memo(({ purchaseType, supplierId, customerId, selectedSupplier, selectedCustomer, onSelectSupplier, onClearSupplier, onClearCustomer, suppliers, suppliersMap }) => {
    if (purchaseType === 'Supplier') {
        return (
            <div className="toolbar-segment" style={{ ...STYLES.segment, flex: '1 1 300px', position: 'relative', zIndex: 2000 }}>
                <span className="toolbar-label">Supplier</span>
                {supplierId ? (
                    <div className="d-flex align-items-center gap-2 w-100 bg-white bg-opacity-10 px-2 rounded border border-white border-opacity-10" style={{ height: '34px' }}>
                        <div style={{ width: 22, height: 22, borderRadius: 4, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.75rem', flexShrink: 0, color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>
                            {selectedSupplier?.business_name?.charAt(0) || 'S'}
                        </div>
                        <div className="flex-grow-1 overflow-hidden d-flex align-items-center gap-2">
                            <div className="toolbar-value" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {selectedSupplier?.business_name}
                            </div>
                            <div style={{ fontSize: '0.65rem', fontWeight: 700, opacity: 0.7, color: '#4ade80', whiteSpace: 'nowrap' }}>
                                <span className="opacity-50 text-white me-1">TEL:</span>{selectedSupplier?.contact_info?.contact_number || 'N/A'}
                            </div>
                        </div>
                        <button type="button" onClick={onClearSupplier}
                            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', borderRadius: 4, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', cursor: 'pointer', flexShrink: 0 }}>
                            ✕
                        </button>
                    </div>
                ) : (
                    <div style={{ width: '100%' }}>
                        <SupplierField selectedSupplierId={supplierId} suppliers={suppliers || []} onAddSupplier={onSelectSupplier} />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="toolbar-segment" style={{ ...STYLES.segment, flex: '1 1 300px' }}>
            <span className="toolbar-label">Customer</span>
            {customerId ? (
                <div className="d-flex align-items-center gap-2 w-100 bg-info bg-opacity-10 px-2 rounded border border-info border-opacity-20" style={{ height: '34px' }}>
                    <div style={{ width: 22, height: 22, borderRadius: 4, background: 'rgba(13,202,240,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.75rem', flexShrink: 0, color: '#0dcaf0' }}>
                        {selectedCustomer?.first_name?.charAt(0)}
                    </div>
                    <div className="flex-grow-1 overflow-hidden d-flex align-items-center gap-2">
                        <div className="toolbar-value" style={{ fontSize: '0.85rem', color: '#0dcaf0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedCustomer?.first_name}</div>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, opacity: 0.8, color: '#0dcaf0', whiteSpace: 'nowrap' }}>
                            <span className="opacity-50 text-white me-1">TEL:</span>{selectedCustomer?.phone_number}
                        </div>
                    </div>
                    <button type="button" onClick={onClearCustomer}
                        style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', borderRadius: 4, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', cursor: 'pointer', flexShrink: 0 }}>
                        ✕
                    </button>
                </div>
            ) : (
                <CustomerSearchField customerId={customerId} name="customer" label="" />
            )}
        </div>
    );
});

// --- SEGMENT C: AGENT ---
export const ToolbarAgent = memo(({ supplierId, agentId, agentName, creditSegment, selectedSupplier, onOpenAgentModal, onChangeAgent }) => {
    if (!supplierId) return null;
    return (
        <div className="toolbar-segment" style={{ ...STYLES.segment, flex: '0 0 180px' }}>
            <div className="d-flex align-items-center justify-content-between">
                <span className="toolbar-label">Sales Rep</span>
                <button type="button" onClick={onOpenAgentModal}
                    style={{ background: 'none', border: 'none', color: '#4caf50', fontSize: '0.65rem', fontWeight: 900, cursor: 'pointer', padding: 0 }}>
                    <BiPlus className="me-1" />ADD NEW
                </button>
            </div>
            <BootstrapForm.Select size="sm" style={STYLES.input} className="sales-rep-select" value={agentId || ""} onChange={(e) => onChangeAgent(e.target.value)}>
                <option value="">Direct (No Agent)</option>
                {selectedSupplier?.contacts?.map((c, i) => <option key={i} value={c._id}>{c.name}</option>)}
            </BootstrapForm.Select>
            <div style={{ fontSize: '0.55rem', opacity: 0.6, marginTop: 1 }}>
                Credit: <span style={{ color: '#90caf9', fontWeight: 700 }}>{creditSegment || 'General'}</span>
            </div>
        </div>
    );
});

// --- SEGMENT D: META ---
export const ToolbarMeta = memo(({
    purchaseDate,
    referenceNumber,
    supplierBillNo,
    onUpdateField,
    onBlurReference
}) => (
    <div className="toolbar-segment toolbar-dark-meta">
        <span className="toolbar-label">Order Details</span>

        <div className="toolbar-row">

            {/* DATE */}
            <div className="toolbar-field fixed-110">
                <div className="toolbar-field-label">DATE</div>
                <DynamicDatePicker fieldName="purchaseDate" label="" />
            </div>

            {/* REF */}
            <div className="toolbar-field flex-1">
                <div className="toolbar-field-label">REF #</div>
                <InputGroup size="sm" className="toolbar-input-group">
                    <InputGroup.Text className="toolbar-prefix">
                        <FaHashtag size={10} />
                    </InputGroup.Text>
                    <FormControl
                        className="toolbar-input"
                        value={referenceNumber}
                        onChange={e => onUpdateField('referenceNumber', e.target.value)}
                        onBlur={onBlurReference}
                        placeholder="REF-XXXXX"
                    />
                </InputGroup>
            </div>

            {/* BILL */}
            <div className="toolbar-field flex-1">
                <div className="toolbar-field-label">BILL #</div>
                <FormControl
                    className="toolbar-input"
                    value={supplierBillNo}
                    onChange={e => onUpdateField('supplier_bill_no', e.target.value)}
                    placeholder="INV-000"
                />
            </div>

        </div>
    </div>
));

// --- SEGMENT E: FINANCIALS ---
export const ToolbarFinancials = memo(({ paymentType, totalVolume, currentBalance, purchaseType, supplierId, setFieldValue, formatCurrency }) => (
    <div className="toolbar-segment" style={{ ...STYLES.segment, flex: '0 0 220px' }}>
        <span className="toolbar-label">Payment</span>
        <div className="d-flex gap-1 mb-1">
            {["Credit", "Cash", "Other"].map(type => (
                <button key={type} type="button" onClick={() => setFieldValue("payment_type", type)}
                    style={{
                        flex: 1, padding: '3px 4px', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer', borderRadius: 3,
                        background: paymentType === type ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.05)',
                        border: paymentType === type ? '1px solid rgba(255,255,255,0.4)' : '1px solid rgba(255,255,255,0.1)',
                        color: paymentType === type ? 'white' : 'rgba(255,255,255,0.5)'
                    }}>
                    {type}
                </button>
            ))}
        </div>
        {purchaseType === 'Supplier' && supplierId && (
            <div className="d-flex gap-3 mt-1">
                <div className="p-1 px-2 rounded" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div style={{ fontSize: '0.5rem', fontWeight: 800, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Volume</div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 900, color: '#4ade80' }}>{formatCurrency(totalVolume || 0)}</div>
                </div>
                <div className="p-1 px-2 rounded" style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)' }}>
                    <div style={{ fontSize: '0.5rem', fontWeight: 800, color: 'rgba(248,113,113,0.8)', textTransform: 'uppercase' }}>Due</div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 900, color: '#f87171' }}>{formatCurrency(currentBalance || 0)}</div>
                </div>
            </div>
        )}
    </div>
));

// --- SEGMENT F: PROOF ---
export const ToolbarProofTrigger = memo(({ showBillProof, onToggle }) => (
    <div className="toolbar-segment" style={{ ...STYLES.segment, flex: '0 0 100px', padding: '0 10px', marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
        <button type="button" onClick={onToggle}
            style={{
                background: showBillProof ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.2)', color: 'white', borderRadius: 6,
                height: '34px', fontSize: '0.62rem', fontWeight: 900, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', width: '100%',
                textTransform: 'uppercase', letterSpacing: '0.05em'
            }}>
            <FaFileInvoiceDollar size={12} />
            PROOF
            {showBillProof ? <BiChevronDown /> : <BiChevronRight />}
        </button>
    </div>
));
